import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import {
  Add as AddIcon,
  Copy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from '@barracuda-internal/bds-core/dist/Icons/Core';

const INITIAL_CONFIRM = { open: false, accountId: null, accountName: '' };
const INITIAL_SNACKBAR = { open: false, type: 'neutralSuccess', message: '' };

const ApiKeysSkeleton = () => {
  const tokens = useTokens();
  return (
    <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Skeleton variant="text" width={140} height={40} />
        <Skeleton variant="rectangular" width={160} height={40} />
      </Stack>
      <Card
        bdsType="staticSubtle"
        bdsBoxShadow="default"
        bdsBorderRadius="round"
        bdsPadding="spacious"
      >
        <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="text" width="80%" height={20} />
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" width="100%" height={72} />
          ))}
        </Stack>
      </Card>
    </Stack>
  );
};

const ApiKeysComponent = ({
  apiKeys,
  newApiKey,
  loading,
  onRegenerateKey,
  onCreateKey,
  onClearNewKey,
}) => {
  const tokens = useTokens();
  const [confirmDialog, setConfirmDialog] = useState(INITIAL_CONFIRM);
  const [createDialog, setCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyTags, setNewKeyTags] = useState('');
  const [nameError, setNameError] = useState(false);
  const [snackbar, setSnackbar] = useState(INITIAL_SNACKBAR);

  if (loading && apiKeys.length === 0) return <ApiKeysSkeleton />;

  const closeCreateDialog = () => {
    setCreateDialog(false);
    setNewKeyName('');
    setNewKeyTags('');
    setNameError(false);
  };

  const closeConfirmDialog = () => setConfirmDialog(INITIAL_CONFIRM);

  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setSnackbar({
      open: true,
      type: 'neutralSuccess',
      message: 'API key copied to clipboard',
    });
  };

  const handleConfirmRegenerate = () => {
    onRegenerateKey(confirmDialog.accountId);
    closeConfirmDialog();
  };

  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      setNameError(true);
      return;
    }
    const tags = newKeyTags
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag) => tag);
    onCreateKey({ name: newKeyName.trim(), tags });
    closeCreateDialog();
  };

  const handleNameChange = (e) => {
    setNewKeyName(e.target.value);
    if (nameError && e.target.value.trim()) setNameError(false);
  };

  return (
    <Stack sx={{ gap: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h2">API keys</Typography>
        <Button
          bdsType="interactiveEmphasis"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create API key
        </Button>
      </Stack>

      {newApiKey && (
        <Alert bdsType="neutralSuccess" onClose={onClearNewKey}>
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
            <Typography variant="body1Bold">{newApiKey.message}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.aliasesSpaceComponentDefault,
                backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
                padding: tokens.aliasesSpaceComponentDefault,
                borderRadius: tokens.aliasesBorderRadiusDefault,
              }}
            >
              <Typography
                variant="code"
                sx={{ flex: 1, wordBreak: 'break-all' }}
              >
                {newApiKey.api_key}
              </Typography>
              <IconButton
                bdsType="interactiveSubtle"
                onClick={() => handleCopyKey(newApiKey.api_key)}
                title="Copy to clipboard"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Stack>
        </Alert>
      )}

      <Card
        bdsType="staticSubtle"
        bdsBoxShadow="default"
        bdsBorderRadius="round"
        bdsPadding="spacious"
      >
        <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
          <Typography variant="captionSubtle">
            Use these API keys to authenticate requests to the Prompt Security Gateway.
          </Typography>

          {apiKeys.length === 0 ? (
            <Box
              sx={{
                padding: tokens.aliasesSpaceContainerDefault,
                textAlign: 'center',
                backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
                borderRadius: tokens.aliasesBorderRadiusDefault,
              }}
            >
              <Typography variant="captionSubtle">
                No API keys found. Create one to get started.
              </Typography>
            </Box>
          ) : (
            <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
              {apiKeys.map((apiKey) => (
                <Box
                  key={apiKey.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: tokens.aliasesSpaceContainerDefault,
                    backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
                    borderRadius: tokens.aliasesBorderRadiusDefault,
                  }}
                >
                  <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                    <Typography variant="body1Bold">{apiKey.name}</Typography>
                    {apiKey.tags && apiKey.tags.length > 0 && (
                      <Stack
                        direction="row"
                        sx={{
                          gap: tokens.aliasesSpaceComponentDense,
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                        }}
                      >
                        {apiKey.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            bdsType="staticNeutral"
                            bdsSize="medium"
                          />
                        ))}
                      </Stack>
                    )}
                    <Typography variant="captionSubtle">
                      Key: {apiKey.api_key_hint} · Created{' '}
                      {new Date(apiKey.created_at).toLocaleDateString()}
                    </Typography>
                  </Stack>
                  <Button
                    bdsType="interactiveNeutral"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() =>
                      setConfirmDialog({
                        open: true,
                        accountId: apiKey.id,
                        accountName: apiKey.name,
                      })
                    }
                  >
                    Regenerate
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        bdsDialogWidth="small"
      >
        <DialogTitle
          dialogTitle={`Regenerate API key for ${confirmDialog.accountName}?`}
          showCloseIcon
          onClose={closeConfirmDialog}
        />
        <DialogContent content="The current key will be invalidated immediately. Any applications using it will stop working." />
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeConfirmDialog}>
            Cancel
          </Button>
          <Button bdsType="interactiveEmphasis" onClick={handleConfirmRegenerate}>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialog}
        onClose={closeCreateDialog}
        bdsDialogWidth="medium"
      >
        <DialogTitle
          dialogTitle="Create API key"
          showCloseIcon
          onClose={closeCreateDialog}
        />
        <DialogContent>
          <Stack
            sx={{
              gap: tokens.aliasesSpaceComponentDefault,
              paddingTop: tokens.aliasesSpaceComponentDense,
            }}
          >
            <TextField
              label="Name"
              value={newKeyName}
              onChange={handleNameChange}
              placeholder="My application"
              error={nameError}
              helperText={nameError ? 'Name is required' : ''}
              fullWidth
              autoFocus
            />
            <TextField
              label="Tags (optional)"
              value={newKeyTags}
              onChange={(e) => setNewKeyTags(e.target.value)}
              placeholder="production, api, backend"
              helperText="Comma-separated tags for organizing your API keys"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeCreateDialog}>
            Cancel
          </Button>
          <Button bdsType="interactiveEmphasis" onClick={handleCreateKey}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div>
          <Alert
            bdsType={snackbar.type}
            bdsBoxShadowType="floating"
            onClose={handleSnackbarClose}
            sx={{ padding: tokens.aliasesSpaceContainerSpacious }}
          >
            {snackbar.message}
          </Alert>
        </div>
      </Snackbar>
    </Stack>
  );
};

export default ApiKeysComponent;
